"use client";
import React, {
  createContext,
  startTransition,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";

export const LayoutContext = createContext();

export const LayoutProvider = ({ children }) => {
  const [offsets, setOffsets] = useState({});
  const [hideFooter, setHideFooter] = useState(false);

  // Offsets are reported by sticky bars as they mount, i.e. during page load
  // while lazy page sections may still be waiting on their chunks to
  // hydrate. An urgent change to this context's value then makes React
  // discard those sections' server HTML and client-render them (removed and
  // re-inserted — a large layout shift). As transitions, React finishes
  // hydrating first; the padding they drive can wait a frame.
  const setOffset = useCallback((id, height) => {
    startTransition(() => {
      setOffsets((prev) => {
        if (prev[id] === height) return prev;
        return { ...prev, [id]: height };
      });
    });
  }, []);

  const removeOffset = useCallback((id) => {
    startTransition(() => {
      setOffsets((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    });
  }, []);

  const totalBottomOffset = useMemo(() => {
    return Object.values(offsets).reduce((acc, curr) => acc + curr, 0);
  }, [offsets]);

  return (
    <LayoutContext.Provider
      value={{
        setOffset,
        removeOffset,
        totalBottomOffset,
        offsets,
        hideFooter,
        setHideFooter,
      }}
    >
      <div
        style={{
          paddingBottom: `${totalBottomOffset}px`,
          transition: "padding 0.3s ease",
        }}
      >
        {children}
      </div>
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};
